import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star, ArrowRight, RefreshCw } from 'lucide-react';
import Button from '../../../components/ui/Button';

const RecommendationEngine = ({ userProfile = {}, availableRoadmaps = [], onRoadmapSelect }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock user preferences and history for recommendations
  const mockUserProfile = {
    experience: userProfile?.experience || 'Beginner',
    interests: userProfile?.interests || ['Manual Testing', 'Automation'],
    completedRoadmaps: userProfile?.completedRoadmaps || [],
    timeAvailable: userProfile?.timeAvailable || 'medium',
    careerGoals: userProfile?.careerGoals || ['QA Engineer'],
    ...userProfile
  };

  const generateRecommendations = () => {
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const scored = availableRoadmaps?.map(roadmap => {
        let score = 0;
        
        // Experience level matching
        if (mockUserProfile?.experience === 'Beginner' && roadmap?.difficulty === 'Beginner') {
          score += 30;
        } else if (mockUserProfile?.experience === 'Intermediate' && 
                   ['Beginner', 'Intermediate']?.includes(roadmap?.difficulty)) {
          score += 25;
        } else if (mockUserProfile?.experience === 'Advanced' && 
                   roadmap?.difficulty !== 'Beginner') {
          score += 20;
        }

        // Interest matching
        const techInterests = mockUserProfile?.interests || [];
        if (techInterests?.some(interest => 
            roadmap?.technology?.toLowerCase()?.includes(interest?.toLowerCase()) ||
            roadmap?.title?.toLowerCase()?.includes(interest?.toLowerCase())
        )) {
          score += 25;
        }

        // Time commitment matching
        if (mockUserProfile?.timeAvailable === 'short' && roadmap?.estimatedTime?.includes('6-8')) {
          score += 20;
        } else if (mockUserProfile?.timeAvailable === 'medium' && roadmap?.estimatedTime?.includes('8-12')) {
          score += 20;
        } else if (mockUserProfile?.timeAvailable === 'long' && roadmap?.estimatedTime?.includes('12+')) {
          score += 20;
        }

        // Popularity boost
        if (roadmap?.trending) score += 10;
        if (roadmap?.featured) score += 15;
        if (roadmap?.rating >= 4.7) score += 10;

        // Enrollment boost for popular courses
        if (roadmap?.enrolled > 10000) score += 5;

        return { ...roadmap, recommendationScore: score };
      });

      // Sort by score and take top 3
      const topRecommendations = scored
        ?.sort((a, b) => b?.recommendationScore - a?.recommendationScore)
        ?.slice(0, 3);

      setRecommendations(topRecommendations);
      setIsLoading(false);
    }, 1000);
  };

  useEffect(() => {
    if (availableRoadmaps?.length > 0) {
      generateRecommendations();
    }
  }, [availableRoadmaps]);

  const getRecommendationReason = (roadmap) => {
    const reasons = [];
    
    if (mockUserProfile?.experience === roadmap?.difficulty) {
      reasons?.push(`Perfect for ${roadmap?.difficulty?.toLowerCase()} level`);
    }
    
    if (roadmap?.trending) {
      reasons?.push('Trending now');
    }
    
    if (roadmap?.rating >= 4.8) {
      reasons?.push('Highly rated');
    }
    
    if (roadmap?.enrolled > 12000) {
      reasons?.push('Popular choice');
    }

    if (mockUserProfile?.interests?.some(interest => 
        roadmap?.technology?.toLowerCase()?.includes(interest?.toLowerCase())
    )) {
      reasons?.push('Matches your interests');
    }

    return reasons?.slice(0, 2)?.join(' • ') || 'Recommended for you';
  };

  if (recommendations?.length === 0 && !isLoading) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-lg">Recommended for You</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="RefreshCw"
          onClick={generateRecommendations}
          disabled={isLoading}
          className={isLoading ? 'animate-spin' : ''}
        >
          Refresh
        </Button>
      </div>
      {/* User Profile Summary */}
      <div className="bg-white/50 rounded-lg p-3 mb-4 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-muted-foreground">Level:</span>
            <span className="ml-1 font-medium">{mockUserProfile?.experience}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Time:</span>
            <span className="ml-1 font-medium capitalize">{mockUserProfile?.timeAvailable}</span>
          </div>
        </div>
        <div className="mt-2">
          <span className="text-muted-foreground">Interests:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {mockUserProfile?.interests?.map((interest, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                {interest}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Loading State */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="bg-white/50 rounded-lg p-3 animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-300 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Recommendations */}
      {!isLoading && recommendations?.length > 0 && (
        <div className="space-y-3">
          {recommendations?.map((roadmap, index) => (
            <motion.div
              key={roadmap?.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/70 rounded-lg p-3 hover:bg-white/90 transition-colors cursor-pointer group"
              onClick={() => onRoadmapSelect(roadmap)}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 ${roadmap?.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                  {React.cloneElement(roadmap?.icon, { className: 'w-4 h-4' })}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-sm group-hover:text-blue-600 transition-colors truncate">
                      {roadmap?.title}
                    </h4>
                    {index === 0 && (
                      <span className="bg-gold-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                        #1 Pick
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                    {roadmap?.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-600 font-medium">
                      {getRecommendationReason(roadmap)}
                    </span>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span>{roadmap?.rating}</span>
                      <span>•</span>
                      <span>{roadmap?.estimatedTime}</span>
                    </div>
                  </div>

                  {/* Quick action buttons */}
                  <div className="flex items-center space-x-2 mt-2">
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Navigate to preview
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      size="xs"
                      iconName="ArrowRight"
                      iconPosition="right"
                      onClick={(e) => {
                        e?.stopPropagation();
                        window.location.href = `/mission-map-dashboard?roadmap=${roadmap?.slug}`;
                      }}
                    >
                      Start
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      {/* Personalization CTA */}
      <div className="mt-4 pt-4 border-t border-white/30">
        <p className="text-xs text-muted-foreground text-center mb-2">
          Get better recommendations by completing your profile
        </p>
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            // Navigate to profile setup
          }}
        >
          Customize Recommendations
        </Button>
      </div>
    </motion.div>
  );
};

export default RecommendationEngine;